/**
 * El `<main data-prerender-shell>` que queda huérfano (JOP-75).
 *
 * Las páginas prerenderizadas llegan con
 *
 *     <main data-prerender-shell><div id="single-spa-application:@jopit/landing" data-prerendered>…</div></main>
 *
 * `layoutEngine.activate()` es sincrónico: crea el `<main>` del layout como
 * `body.firstChild` y MUEVE adentro el contenedor existente (lo reconoce por id).
 * El `<main>` del prerender queda vacío, y con `main { height: 100dvh }` empujaría
 * la página hacia abajo. Se llama justo después de `activate()`:
 *
 * - si el contenedor ya no está adentro del shell, se borra el shell;
 * - si sigue adentro (el layout no lo tomó), no se toca: borrarlo dejaría la página
 *   sin contenido. Se avisa en consola.
 */

export const PRERENDER_SHELL_SELECTOR = "[data-prerender-shell]";
export const LANDING_CONTAINER_ID = "single-spa-application:@jopit/landing";

/**
 * @param {Document} doc
 * @param {{ warn: (...args: unknown[]) => void }} logger
 * @returns {"none" | "removed" | "kept"}
 */
export function removeOrphanPrerenderShell(doc = document, logger = console) {
    const shell = doc.querySelector(PRERENDER_SHELL_SELECTOR);
    if (!shell) {
        return "none";
    }

    const container = doc.getElementById(LANDING_CONTAINER_ID);
    if (container && shell.contains(container)) {
        logger.warn("[root-config] el contenedor del landing sigue dentro de <main data-prerender-shell> después de activate(): no se borra.");
        return "kept";
    }

    shell.remove();
    return "removed";
}

/**
 * Adopta el `<main data-prerender-shell>` como el `<main>` del layout, ANTES de
 * `layoutEngine.activate()`.
 *
 * Sin esto, `activate()` crea su propio `<main>` y MUEVE adentro el contenedor del
 * landing. Chrome trata un subárbol movido como contenido nuevo y vuelve a registrar su
 * pintado para LCP: si para entonces Inter ya llegó (swap) y agrandó el titular del
 * hero, ese re-registro es un candidato a LCP más grande, ~300 ms después del primer
 * paint, y PSI lo encadena detrás del JS (home: Perf 99 → 90, LCP 1,8 → 3,6 s;
 * laboratorio en .claude/session/metrics-seo-prerender-ssg.md del umbrella).
 *
 * single-spa-layout 1.6.0 reutiliza el nodo DOM que un elemento del layout tenga en
 * `connectedNode` en lugar de clonarlo, y sólo hace `insertBefore` si el nodo no está ya
 * en su lugar. Con el shell como `connectedNode` del `<main>` (y el shell como primer
 * hijo del body), `activate()` no mueve nada: el contenedor ya está adentro, donde va.
 * Se le saca `data-prerender-shell` porque desde acá es el `<main>` del layout, así
 * que `removeOrphanPrerenderShell` ya no lo encuentra.
 *
 * Si el shell no es el primer hijo con contenido del body, no se adopta: el layout lo
 * movería igual, y se mantiene el camino de siempre (crear, mover y borrar el huérfano).
 *
 * @param {{ routes: Array<any> }} routes resultado de `constructRoutes`
 * @param {Document} doc
 * @returns {"adopted" | "none" | "skipped"}
 */
export function adoptPrerenderShell(routes, doc = document) {
    const shell = doc.querySelector(PRERENDER_SHELL_SELECTOR);
    if (!shell) {
        return "none";
    }

    const layoutMain = (routes.routes || []).find((node) => nodeName(node) === "main");
    const container = doc.getElementById(LANDING_CONTAINER_ID);
    if (!layoutMain || layoutMain.connectedNode || !container || !shell.contains(container) || !isFirstContentOfBody(shell, doc)) {
        return "skipped";
    }

    // El layout ubica su <main> como primer hijo del body: con los saltos de línea del
    // template adelante, lo movería igual. Sacar texto vacío no mueve el shell.
    while (doc.body.firstChild !== shell) {
        doc.body.removeChild(doc.body.firstChild);
    }
    shell.removeAttribute("data-prerender-shell");
    layoutMain.connectedNode = shell;
    return "adopted";
}

function nodeName(node) {
    return String(node && (node.nodeName || node.type) || "").toLowerCase();
}

function isFirstContentOfBody(element, doc) {
    for (let node = doc.body.firstChild; node; node = node.nextSibling) {
        if (node === element) {
            return true;
        }
        if (node.nodeType !== 3 || node.textContent.trim() !== "") {
            return false;
        }
    }
    return false;
}
